import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';

import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import {
    Box,
    Typography,
    Link,
    IconButton,
    useTheme,
    alpha,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
    Menu,
    MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

const Header = () => {
    const [activeMenu, setActiveMenu] = useState(null);
    const [activeSection, setActiveSection] = useState('hero'); // Default to hero/top
    const [loginAnchorEl, setLoginAnchorEl] = useState(null);
    const openLogin = Boolean(loginAnchorEl);
    const [currentLang, setCurrentLang] = useState('pt');
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const headerRef = React.useRef(null);

    // 1. Dynamic Header Height Strategy (Removed - Delegated to AppShell)

    // 2. Scroll Detection (Retargeted to .scroller)
    React.useEffect(() => {
        // Find the global scroller container
        const scroller = document.querySelector('.scroller');
        if (!scroller) return;

        const handleScroll = () => {
            // Only tracking sections on valid pages (mostly Home)
            if (location.pathname !== '/') {
                setActiveSection(null);
                return;
            }

	            const sections = [
	                'hero',
	                'news-grid',
	                'o-que-fazemos',
	                'ciencia-tecnologia'
	            ];

	            // const scrollY = scroller.scrollTop;
	            // const viewportHeight = scroller.clientHeight; // Not needed for simple spy

            let current = 'hero';
            let minDistance = Infinity;

            for (const id of sections) {
                const el = document.getElementById(id);
                if (el) {
                    // Start of section relative to scroller container
                    // Since scroller needs position:relative for this to work simply,
                    // or we check relative position to top of viewport.
                    // .getBoundingClientRect().top is relative to viewport.
                    // If section is at top of viewport (under header), rect.top approx headerHeight

                    const rect = el.getBoundingClientRect();
                    // We want the section that is closest to the "snap point"
                    // The snap point is the header bottom (approx 70-100px)
                    const snapPoint = 100; // Heuristic
                    const dist = Math.abs(rect.top - snapPoint);

                    if (dist < minDistance) {
                        minDistance = dist;
                        current = id;
                    }
                }
            }
            setActiveSection(current);
        };

        scroller.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check
        return () => scroller.removeEventListener('scroll', handleScroll);
    }, [location.pathname]);

    // Auth Hooks
    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();

    const handleMouseEnter = (menu) => {
        setActiveMenu(menu);
    };

    const handleMouseLeave = () => {
        setActiveMenu(null);
    };

    const changeLanguage = (lang) => {
        if (lang === 'pt') {
            document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' + document.domain;
            window.location.reload();
            return;
        }

        const combo = document.querySelector('.goog-te-combo');
        if (combo) {
            combo.value = lang;
            combo.dispatchEvent(new Event('change'));
        }
        setCurrentLang(lang);
    };

    const handleLogout = () => {
        instance.logoutPopup().catch(e => {
            console.error(e);
        });
    };

    const languages = [
        { code: 'pt', label: 'PT' },
        { code: 'en', label: 'EN' },
        { code: 'es', label: 'ES' }
    ];

    const availableLanguages = languages.filter(lang => lang.code !== currentLang);

    const closeMenu = () => {
        setActiveMenu(null);
    };

    const [mobileOpen, setMobileOpen] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleScrollTop = () => {
        const scroller = document.querySelector('.scroller');
        if (scroller) scroller.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleLoginNavigate = (to) => (e) => {
        // If the Menu is visually open but clicks are leaking to underlying anchors,
        // prevent the "scroll to footer" behavior.
        if (e?.preventDefault) e.preventDefault();
        if (e?.stopPropagation) e.stopPropagation();
        setLoginAnchorEl(null);
        navigate(to);
    };

    // ... (existing scroll logic)

    // Mobile Menu Drawer Content
    const drawer = (
        <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
            <Box sx={{ my: 2 }}>
                <Box
                    component="img"
                    src="/images/logo-r5-white.png"
                    alt="R5 Inteligência Digital"
                    sx={{ height: 40 }}
                />
            </Box>
            <Divider />
	            <List>
	                {[
	                    { label: 'HOME', to: '/' },
	                    { label: 'Sobre Nós', to: '/visao-geral' }, // Renamed from Quem Somos
		                    { label: 'NOTÍCIAS', to: '/#news-grid' }, // Reordered & Renamed
	                    { label: 'Áreas de Atuação', to: '/#o-que-fazemos' },
	                    { label: 'PESQUISA E INOVAÇÃO', to: '/#ciencia-tecnologia' }, // Renamed from Nossa Tecnologia
	                    { label: 'Fale Conosco', to: '/fale-conosco' },
                        { label: 'Canal de Ética', to: '/canal-de-etica' },
	                ].map((item) => (
	                    <ListItem key={item.label} disablePadding>
                        <ListItemButton
                            component={RouterLink}
                            to={item.to}
                            onClick={() => {
                                if (item.to === '/') handleScrollTop();
                            }}
                            sx={{ textAlign: 'center' }}
                        >
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <>
            <Box
                ref={headerRef}
                component="header"
                data-ui="header"
                onMouseLeave={handleMouseLeave}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'common.black',
                    fontFamily: "'Inter', sans-serif",
                    position: 'relative', // Changed from fixed to relative/static for AppShell
                    top: 0,
                    left: 0,
                    right: 0,
                    width: '100%',
                    zIndex: 2000,
                    backgroundColor: 'background.paper',
                    boxShadow: theme.shadows[1], // Restore shadow for depth
                    borderBottom: 'none'
                }}
            >
                {/* Header Top */}
                <Box
                    data-ui="header-top"
                    sx={{
                        backgroundColor: 'common.black',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: '5%',
                        height: 40,
                        fontSize: '0.80rem',
                        fontWeight: 500,
                        borderBottom: 1,
                        borderColor: alpha(theme.palette.common.white, 0.1),
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* Home Icon Removed */}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* Only show language switcher on non-mobile for cleaner mobile topbar, or keep it */}
                        {availableLanguages.map(lang => (
                            <Link
                                key={lang.code}
                                href={`#${lang.label.toLowerCase()}`}
                                data-ui={`lang-switch-${lang.code}`}
                                onClick={(e) => { e.preventDefault(); changeLanguage(lang.code); }}
                                sx={{
                                    color: 'grey.300',
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    ml: 2.5,
                                    transition: 'color 0.2s',
                                    '&:hover': { color: 'common.white' }
                                }}
                            >
                                {lang.label}
                            </Link>
                        ))}

                        {isAuthenticated ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2.5 }}>
                                <IconButton
                                    component={RouterLink}
                                    to="/admin"
                                    title="Admin"
                                    data-ui="btn-admin"
                                    sx={{
                                        color: 'grey.400',
                                        p: 0.6,
                                        '&:hover': { color: 'common.white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                    }}
                                >
                                    <SettingsIcon sx={{ fontSize: '1.1rem' }} />
                                </IconButton>
                                <IconButton
                                    onClick={handleLogout}
                                    title="Sair"
                                    data-ui="btn-logout"
                                    sx={{
                                        color: 'grey.400',
                                        p: 0.6,
                                        '&:hover': { color: 'common.white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                    }}
                                >
                                    <LogoutIcon sx={{ fontSize: '1.1rem' }} />
                                </IconButton>
                            </Box>
                        ) : (
                            <>
                                <Box
                                    component="span"
                                    onClick={(e) => setLoginAnchorEl(e.currentTarget)}
                                    data-ui="link-login"
                                    sx={{
                                        color: 'grey.300',
                                        textDecoration: 'none',
                                        transition: 'color 0.2s',
                                        cursor: 'pointer',
                                        ml: 2.5,
                                        '&:hover': {
                                            color: 'common.white'
                                        }
                                    }}
                                >
                                    Login
                                </Box>
                                <Menu
                                    anchorEl={loginAnchorEl}
                                    open={openLogin}
                                    onClose={() => setLoginAnchorEl(null)}
                                    MenuListProps={{
                                        'aria-labelledby': 'basic-button',
                                        sx: { py: 0 }
                                    }}
                                    PaperProps={{
                                        sx: {
                                            mt: 1,
                                            backgroundColor: 'rgba(20, 20, 20, 0.95)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            color: 'common.white',
                                            '& .MuiMenuItem-root': {
                                                fontSize: '0.85rem',
                                                color: 'common.white',
                                                py: 1.5,
                                                px: 3,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.05)'
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem
                                        onClick={handleLoginNavigate('/portal-cliente')}
                                    >
                                        Sou Cliente
                                    </MenuItem>
                                    <Divider sx={{ my: 0, borderColor: 'rgba(255,255,255,0.1)' }} />
                                    <MenuItem
                                        onClick={handleLoginNavigate('/login')}
                                    >
                                        Sou Colaborador
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                </Box>

                {/* Header Main */}
                <Box
                    data-ui="header-main"
                    sx={{
                        backgroundColor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between', // Ensure logic separates Logo (Start) - Nav (Center/End) - Search (End)
                        px: { xs: 2, md: '5%' },
                        height: 70,
                        position: 'relative',
                        zIndex: 1100,
                        borderBottom: 'none'
                    }}
                >
                    {/* MOBILE: Hamburger Button */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ color: 'common.black' }}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Box>

                    {/* Logo */}
                    <Link component={RouterLink} to="/" onClick={handleScrollTop} sx={{ display: 'flex', alignItems: 'center', mr: { xs: 1, md: 5 } }} data-ui="logo-link">
                        <Box
                            component="img"
                            src="/images/logo-r5-white.png"
                            alt="R5 Inteligência Digital"
                            sx={{
                                height: { xs: 32, md: 38 }, // Slightly smaller on mobile
                                display: 'block',
                                // filter: 'invert(1) hue-rotate(180deg)', // Filter removed for new logo asset
                            }}
                        />
                    </Link>

                    {/* DESKTOP: Main Navigation */}
                    <Box component="nav" sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1, gap: 0.5, height: '100%', alignItems: 'center' }}>
                        {/* HOME */}
                        <Box
                            onMouseEnter={() => setActiveMenu(null)}
                            data-ui="nav-item-home"
                            sx={{
                                position: 'relative',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '0 12px',
                                '&:hover::after': { transform: 'scaleX(1)' },
                                '&::after': {
                                    content: "''",
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 3,
                                    backgroundColor: 'primary.main',
                                    transform: activeSection === 'hero' ? 'scaleX(1)' : 'scaleX(0)',
                                    transition: 'transform 0.2s ease-out',
                                }
                            }}
                        >
                            <Link
                                component={RouterLink}
                                to="/"
                                onClick={handleScrollTop}
                                variant="overline"
                                sx={{
                                    color: activeSection === 'hero' ? 'primary.main' : 'common.black',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    whiteSpace: 'nowrap',
                                    transition: 'color 0.2s',
                                    '&:hover': { color: 'primary.main' }
                                }}
                            >
                                HOME
                            </Link>
                        </Box>


                        {/* Sobre Nós (Previously Quem Somos) */}
                        <Box
                            onMouseEnter={() => handleMouseEnter('quem-somos')}
                            data-ui="nav-item-quem-somos"
                            sx={{
                                position: 'relative',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '0 12px',
                                marginLeft: { xs: 0, lg: '48px' }, // Responsive Isolation
                                '&:hover::after': { transform: 'scaleX(1)' },
                                '&::after': {
                                    content: "''",
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 3,
                                    backgroundColor: 'primary.main',
                                    transform: 'scaleX(0)',
                                    transition: 'transform 0.2s ease-out',
                                }
                            }}
                        >
                            <Typography
                                component="span"
                                variant="overline"
                                sx={{
                                    color: 'common.black',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    transition: 'color 0.2s',
                                    '&:hover': { color: 'primary.main' }
                                }}
                            >
                                Sobre Nós
                            </Typography>
                            {activeMenu === 'quem-somos' && (
                                <Box
                                    data-ui="dropdown-quem-somos"
                                    sx={{
                                        position: 'absolute',
                                        top: 70,
                                        left: 0,
                                        backgroundColor: 'background.paper',
                                        py: 2.5,
                                        minWidth: 240,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxShadow: theme.shadows[4],
                                        zIndex: 2010,
                                        borderTop: `3px solid ${theme.palette.primary.main}`,
                                        '& a': {
                                            color: 'text.secondary',
                                            fontSize: '0.9rem',
                                            fontWeight: 500,
                                            textDecoration: 'none',
                                            px: 3,
                                            py: 1.25,
                                            transition: 'all 0.2s',
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            '&:last-child': { borderBottom: 'none' },
                                            '&:hover': {
                                                color: 'primary.main',
                                                backgroundColor: 'action.hover',
                                                pl: 3.5
                                            }
                                        }
                                    }}
                                >
                                    <Link component={RouterLink} to="/visao-geral" onClick={closeMenu}>O que fazemos</Link>
	                                    {/* Nossa História (temporariamente oculto nesta versão) */}
                                    <Link component={RouterLink} to="/proposito" onClick={closeMenu}>Propósito e Missão</Link>
                                    <Link component={RouterLink} to="/governanca" onClick={closeMenu}>Programa de Integridade</Link>
                                    <Link component={RouterLink} to="/canal-de-etica" onClick={closeMenu}>Canal de Ética</Link>
                                    <Link component={RouterLink} to="/fale-conosco" onClick={closeMenu}>Fale Conosco</Link>
                                </Box>
                            )}
                        </Box>

                        {/* Notícias e Atualizações */}
                        <Box
                            onMouseEnter={() => setActiveMenu(null)}
                            data-ui="nav-item-noticias"
                            sx={{
                                position: 'relative',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '0 12px',
                                '&:hover::after': { transform: 'scaleX(1)' },
                                '&::after': {
                                    content: "''",
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 3,
                                    backgroundColor: 'primary.main',
                                    transform: activeSection === 'news-grid' ? 'scaleX(1)' : 'scaleX(0)',
                                    transition: 'transform 0.2s ease-out',
                                }
                            }}
                        >
                            <Link
                                component={RouterLink}
                                to="/#news-grid"
                                variant="overline"
                                sx={{
                                    color: activeSection === 'news-grid' ? 'primary.main' : 'common.black',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    whiteSpace: 'nowrap',
                                    transition: 'color 0.2s',
                                    '&:hover': { color: 'primary.main' }
                                }}
	                            >
	                                NOTÍCIAS
	                            </Link>
	                        </Box>

                        {/* O Que Fazemos */}
                        <Box
                            onMouseEnter={() => setActiveMenu(null)}
                            data-ui="nav-item-o-que-fazemos"
                            sx={{
                                position: 'relative',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '0 12px',
                                '&:hover::after': { transform: 'scaleX(1)' },
                                '&::after': {
                                    content: "''",
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 3,
                                    backgroundColor: 'primary.main',
                                    transform: activeSection === 'o-que-fazemos' ? 'scaleX(1)' : 'scaleX(0)',
                                    transition: 'transform 0.2s ease-out',
                                }
                            }}
                        >
                            <Link
                                component={RouterLink}
                                to="/#o-que-fazemos"
                                variant="overline"
                                sx={{
                                    color: activeSection === 'o-que-fazemos' ? 'primary.main' : 'common.black',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    whiteSpace: 'nowrap',
                                    transition: 'color 0.2s',
                                    '&:hover': { color: 'primary.main' }
                                }}
                            >
                                Áreas de Atuação
                            </Link>
                        </Box>

	                        {/* Inovação & Tecnologia */}
	                        <Box
	                            onMouseEnter={() => setActiveMenu(null)}
	                            data-ui="nav-item-ciencia-tecnologia"
                            sx={{
                                position: 'relative',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '0 12px',
                                '&:hover::after': { transform: 'scaleX(1)' },
                                '&::after': {
                                    content: "''",
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 3,
                                    backgroundColor: 'primary.main',
                                    transform: (activeSection === 'ciencia-tecnologia' || activeSection === 'innovation-monitor') ? 'scaleX(1)' : 'scaleX(0)',
                                    transition: 'transform 0.2s ease-out',
                                }
                            }}
                        >
                            <Link
                                component={RouterLink}
                                to="/#ciencia-tecnologia"
                                variant="overline"
                                sx={{
                                    color: (activeSection === 'ciencia-tecnologia' || activeSection === 'innovation-monitor') ? 'primary.main' : 'common.black',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    whiteSpace: 'nowrap',
                                    transition: 'color 0.2s',
                                    '&:hover': { color: 'primary.main' }
                                }}
	                            >
	                                PESQUISA E INOVAÇÃO
	                            </Link>
	                        </Box>



                        {/* Fale Conosco */}
                        <Box
                            onMouseEnter={() => setActiveMenu(null)}
                            data-ui="nav-item-fale-conosco"
                            sx={{
                                position: 'relative',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '0 12px',
                                '&:hover::after': { transform: 'scaleX(1)' },
                                '&::after': {
                                    content: "''",
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 3,
                                    backgroundColor: 'primary.main',
                                    transform: 'scaleX(0)',
                                    transition: 'transform 0.2s ease-out',
                                }
                            }}
                        >
                            <Link
                                component={RouterLink}
                                to="/fale-conosco"
                                variant="overline"
                                sx={{
                                    color: 'common.black',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    whiteSpace: 'nowrap',
                                    transition: 'color 0.2s',
                                    '&:hover': { color: 'primary.main' }
                                }}
                            >
                                Fale Conosco
                            </Link>
                        </Box>
                    </Box>

                </Box>
            </Box>

            {/* Mobile Navigation Drawer */}
            <Box component="nav">
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* GHOST SPACER REMOVED */}
        </>
    );
};

export default Header;
